import { Action, configureStore, ThunkAction } from "@reduxjs/toolkit";
import { createReduxEnhancer } from "@sentry/react";
import { useDispatch, useSelector, useStore } from "react-redux";

import { backendApi } from "./api/enhancedApi";

import { setupListeners } from "@reduxjs/toolkit/query";
import versionSlice from "store/slices/versionSlice.ts";
import { deployPlanFormSlice } from "./slices/deployPlanFormSlice";
import { planMixTableEditsSlice } from "./slices/planMixesTableEditSlice";
import { settingsSlice } from "./slices/settings";
import { sideMenuSlice } from "./slices/sideMenuSlice";
import { steelGradeSearchSlice } from "./slices/steelGradeSearchSlice";
import connectionSlice from "./slices/connectionSlice";
import { connectionMiddleware } from "./middleware/connectionMiddleware";
import { scrapTrackingViewportSlice } from "./slices/scrapTrackingViewportSlice";

export const store = configureStore({
  reducer: {
    settings: settingsSlice.reducer,
    sideMenu: sideMenuSlice.reducer,
    version: versionSlice,
    connection: connectionSlice,
    [steelGradeSearchSlice.reducerPath]: steelGradeSearchSlice.reducer,
    [planMixTableEditsSlice.reducerPath]: planMixTableEditsSlice.reducer,
    [deployPlanFormSlice.reducerPath]: deployPlanFormSlice.reducer,
    scrapTrackingViewport: scrapTrackingViewportSlice.reducer,
    [backendApi.reducerPath]: backendApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // All fulfilled API query actions and cached query state are exempt because
        // file-download queries may contain Blob values.
        ignoredActions: ["api/executeQuery/fulfilled"],
        ignoredPaths: ["api.queries"],
      },
    }).concat(backendApi.middleware, connectionMiddleware.middleware),
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat(createReduxEnhancer()),
});

setupListeners(store.dispatch);

export type AppStore = typeof store;
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
export const useAppStore = useStore.withTypes<AppStore>();
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action
>;
